import LegalPageLayout from '@/components/layout/LegalPageLayout';

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'calendars', label: 'Calendars' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'teams', label: 'Teams' },
  { id: 'organizations', label: 'Organizations' },
  { id: 'rate-limits', label: 'Rate Limits' },
  { id: 'errors', label: 'Errors' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="reveal py-10 border-b border-white/[0.04]">
      <h2 className="text-[22px] font-semibold tracking-tight leading-snug mb-5 text-white scroll-mt-[110px]">
        {title}
      </h2>
      <div className="text-sm text-zinc-500 leading-[1.75]">{children}</div>
    </section>
  );
}

function InfoCard({ children, accent }) {
  const border = accent ? 'border-white/[0.12]' : 'border-white/[0.06]';
  const bg = accent ? 'bg-white/[0.04]' : 'bg-white/[0.02]';
  return (
    <div className={`rounded-2xl border ${border} ${bg} p-5 my-5 transition-colors hover:border-white/[0.12]`}>
      {children}
    </div>
  );
}

function CodeBlock({ children }) {
  return (
    <pre className="bg-black/40 border border-white/[0.06] rounded-xl p-4 my-4 overflow-x-auto text-xs font-mono text-zinc-300 leading-relaxed">
      <code>{children}</code>
    </pre>
  );
}

export default function APIDocs() {
  return (
    <LegalPageLayout
      title="API Documentation"
      subtitle="Build custom integrations with the ScheduleX REST API. All endpoints return JSON and use standard HTTP methods."
      tocItems={tocItems}
    >
      <Section id="overview" title="Overview">
        <p className="mb-4">
          The ScheduleX API is organized around REST. Our API has predictable resource-oriented URLs, accepts JSON-encoded request bodies, returns JSON-encoded responses, and uses standard HTTP response codes, authentication, and verbs.
        </p>
        <InfoCard accent>
          <p className="font-medium text-white mb-2">Base URL</p>
          <code className="text-xs font-mono text-zinc-300 bg-black/40 px-3 py-1.5 rounded-lg border border-white/[0.06]">
            https://www.schedulex.site/api
          </code>
        </InfoCard>
        <InfoCard>
          <p className="font-medium text-white mb-2">Content Type</p>
          <p>All requests must include the header <code className="text-xs font-mono text-zinc-300 bg-black/40 px-2 py-0.5 rounded">Content-Type: application/json</code>.</p>
        </InfoCard>
      </Section>

      <Section id="authentication" title="Authentication">
        <p className="mb-4">
          ScheduleX uses Bearer token authentication. Include your API token in the Authorization header of every request.
        </p>
        <InfoCard accent>
          <p className="font-medium text-white mb-2">Getting Your Token</p>
          <p>Go to Settings &gt; API Keys to generate a personal access token. Treat this token like a password — do not share it or commit it to version control.</p>
        </InfoCard>
        <CodeBlock>{`GET /api/calendars
Authorization: Bearer YOUR_API_TOKEN
Content-Type: application/json`}</CodeBlock>
        <InfoCard>
          <p className="font-medium text-white mb-1">Token Security</p>
          <p>Tokens do not expire automatically but can be revoked at any time from the API Keys page. If you suspect a token has been compromised, revoke it immediately and generate a new one.</p>
        </InfoCard>
      </Section>

      <Section id="calendars" title="Calendars">
        <p className="mb-4">Manage booking calendars — create, update, list, and delete.</p>

        <InfoCard>
          <p className="font-medium text-white mb-1">GET /api/calendars</p>
          <p className="mb-2">List all calendars for the authenticated user.</p>
          <CodeBlock>{`{
  "success": true,
  "data": [
    {
      "_id": "cal_abc123",
      "name": "Sales Calls",
      "slug": "sales-calls",
      "duration": 30,
      "isActive": true
    }
  ]
}`}</CodeBlock>
        </InfoCard>

        <InfoCard>
          <p className="font-medium text-white mb-1">POST /api/calendars</p>
          <p className="mb-2">Create a new calendar.</p>
          <CodeBlock>{`{
  "name": "Discovery Calls",
  "duration": 45,
  "description": "Initial discovery call with prospects",
  "slug": "discovery"
}`}</CodeBlock>
        </InfoCard>

        <InfoCard>
          <p className="font-medium text-white mb-1">PUT /api/calendars/:id</p>
          <p className="mb-2">Update an existing calendar.</p>
          <CodeBlock>{`{
  "name": "Updated Name",
  "duration": 60,
  "bufferTime": 15
}`}</CodeBlock>
        </InfoCard>

        <InfoCard>
          <p className="font-medium text-white mb-1">DELETE /api/calendars/:id</p>
          <p>Permanently delete a calendar and all associated bookings. This action cannot be undone.</p>
        </InfoCard>
      </Section>

      <Section id="bookings" title="Bookings">
        <p className="mb-4">Retrieve and manage bookings made on your calendars.</p>

        <InfoCard>
          <p className="font-medium text-white mb-1">GET /api/bookings</p>
          <p className="mb-2">List bookings with optional filters.</p>
          <CodeBlock>{`GET /api/bookings?calendar=cal_abc123&status=confirmed&from=2026-01-01`}</CodeBlock>
        </InfoCard>

        <InfoCard>
          <p className="font-medium text-white mb-1">POST /api/bookings</p>
          <p className="mb-2">Create a booking programmatically.</p>
          <CodeBlock>{`{
  "calendar": "cal_abc123",
  "inviteeName": "Jane Doe",
  "inviteeEmail": "jane@example.com",
  "startTime": "2026-06-25T14:00:00Z",
  "notes": "Discuss Q3 roadmap"
}`}</CodeBlock>
        </InfoCard>

        <InfoCard>
          <p className="font-medium text-white mb-1">PUT /api/bookings/:id/status</p>
          <p className="mb-2">Update booking status (confirmed, cancelled, completed).</p>
          <CodeBlock>{`{
  "status": "cancelled",
  "reason": "Schedule conflict"
}`}</CodeBlock>
        </InfoCard>
      </Section>

      <Section id="teams" title="Teams">
        <p className="mb-4">Manage team members and invitations.</p>

        <InfoCard>
          <p className="font-medium text-white mb-1">GET /api/teams</p>
          <p className="mb-2">Get current team details and member list.</p>
        </InfoCard>

        <InfoCard>
          <p className="font-medium text-white mb-1">POST /api/teams/invite</p>
          <p className="mb-2">Invite a new member by email.</p>
          <CodeBlock>{`{
  "email": "newmember@example.com",
  "role": "member"
}`}</CodeBlock>
        </InfoCard>

        <InfoCard>
          <p className="font-medium text-white mb-1">PUT /api/teams/members/:id</p>
          <p className="mb-2">Update a member&apos;s role.</p>
          <CodeBlock>{`{
  "role": "admin"
}`}</CodeBlock>
        </InfoCard>
      </Section>

      <Section id="organizations" title="Organizations">
        <p className="mb-4">Manage organization-level settings and branding.</p>

        <InfoCard>
          <p className="font-medium text-white mb-1">GET /api/organizations/current</p>
          <p className="mb-2">Get current organization details.</p>
        </InfoCard>

        <InfoCard>
          <p className="font-medium text-white mb-1">PUT /api/organizations/:id</p>
          <p className="mb-2">Update organization settings.</p>
          <CodeBlock>{`{
  "name": "Acme Corp",
  "slug": "acme-corp",
  "settings": {
    "timezone": "America/New_York",
    "weekStart": "monday"
  }
}`}</CodeBlock>
        </InfoCard>
      </Section>

      <Section id="rate-limits" title="Rate Limits">
        <p className="mb-4">To ensure stability for all users, the API enforces rate limits.</p>

        <InfoCard accent>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="px-3 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="text-lg font-bold text-white">100</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">req/min</div>
            </div>
            <div className="px-3 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="text-lg font-bold text-white">5,000</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">req/day</div>
            </div>
            <div className="px-3 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              <div className="text-lg font-bold text-white">20</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">req/sec burst</div>
            </div>
          </div>
        </InfoCard>
        <p>If you exceed the rate limit, you will receive a <code className="text-xs font-mono text-zinc-300 bg-black/40 px-2 py-0.5 rounded">429 Too Many Requests</code> response. The <code className="text-xs font-mono text-zinc-300 bg-black/40 px-2 py-0.5 rounded">Retry-After</code> header indicates when you can make your next request.</p>
      </Section>

      <Section id="errors" title="Errors">
        <p className="mb-4">ScheduleX uses conventional HTTP response codes to indicate the success or failure of an API request.</p>

        <InfoCard>
          <ul className="space-y-2 text-xs font-mono">
            <li className="flex items-start gap-3"><span className="w-8 text-emerald-400 shrink-0">200</span><span className="text-zinc-400">OK — Request succeeded</span></li>
            <li className="flex items-start gap-3"><span className="w-8 text-emerald-400 shrink-0">201</span><span className="text-zinc-400">Created — Resource created successfully</span></li>
            <li className="flex items-start gap-3"><span className="w-8 text-yellow-400 shrink-0">400</span><span className="text-zinc-400">Bad Request — Invalid parameters</span></li>
            <li className="flex items-start gap-3"><span className="w-8 text-yellow-400 shrink-0">401</span><span className="text-zinc-400">Unauthorized — Invalid or missing token</span></li>
            <li className="flex items-start gap-3"><span className="w-8 text-yellow-400 shrink-0">403</span><span className="text-zinc-400">Forbidden — Insufficient permissions</span></li>
            <li className="flex items-start gap-3"><span className="w-8 text-yellow-400 shrink-0">404</span><span className="text-zinc-400">Not Found — Resource does not exist</span></li>
            <li className="flex items-start gap-3"><span className="w-8 text-red-400 shrink-0">429</span><span className="text-zinc-400">Too Many Requests — Rate limit exceeded</span></li>
            <li className="flex items-start gap-3"><span className="w-8 text-red-400 shrink-0">500</span><span className="text-zinc-400">Server Error — Something went wrong on our end</span></li>
          </ul>
        </InfoCard>
      </Section>
    </LegalPageLayout>
  );
}
